<!-- Fixed NewChatBody.vue with FAQ navigation -->
<template>
  <div
    class="flex-1 p-4 overflow-y-auto bg-neutral-50"
    :class="containerClasses"
    ref="chatContainer"
  >
    <!-- Message Renderer Component with FAQ navigation -->
    <ChatMessageRenderer
      :messages="messages"
      :styles="styles"
      @open-faq="handleFaqNavigation"
    />

    <!-- Waiting for Support Bar -->
    <ChatWaitingBar v-if="showWaitingBar" />

    <!-- Chat Phase Components -->
    <ChatStart
      v-if="currentPhase === 'start' || showTransitionForm"
      :key="`start-${componentKey}`"
      :styles="styles"
      :customer-name="customerName"
      :company-id="companyId"
      @add-message="messageHandlers.addMessage"
      @remove-typing="messageHandlers.removeTypingMessage"
      @phase-complete="phaseManager.handlePhaseComplete"
      @form-submitted="eventHandlers.handleFormSubmitted"
    />

    <component
      v-else
      :is="currentPhaseComponent"
      :key="`${currentPhase}-${conversation?.$id || 'no-conv'}-${componentKey}`"
      :styles="styles"
      :messages="messages"
      :customer-name="customerName"
      :customer="customer"
      :conversation="conversation"
      :company-id="companyId"
      @add-message="messageHandlers.addMessage"
      @remove-typing="messageHandlers.removeTypingMessage"
      @phase-complete="phaseManager.handlePhaseComplete"
      @form-submitted="eventHandlers.handleFormSubmitted"
      @mood-submitted="eventHandlers.handleMoodSubmitted"
      @escalate-to-human="eventHandlers.handleEscalateToHuman"
      @agent-joined="eventHandlers.handleAgentJoined"
      @rating-submitted="eventHandlers.handleRatingSubmitted"
      @rating-skipped="eventHandlers.handleRatingSkipped"
      @chat-complete="eventHandlers.handleChatComplete"
      @restart-chat="handleRestartWithTransition"
      @continue-waiting="timeoutManager.handleContinueWaiting"
      @email-sent="eventHandlers.handleEmailSent"
    />
  </div>
</template>

<script>
import {
  ref,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
  nextTick,
} from "vue";

// Phase Components
import ChatStart from "./ChatPhase/ChatStart.vue";
import ChatAI from "./ChatPhase/ChatAI.vue";
import ChatHuman from "./ChatPhase/ChatHuman.vue";
import ChatEnd from "./ChatPhase/ChatEnd.vue";
import AgentTimeout from "./AgentTimeout.vue";

//UI
import ChatMessageRenderer from "./ChatMessageRenderer.vue";
import ChatWaitingBar from "./ChatWaitingBar.vue";

// Composables
import { useChatMessages } from "../../composables/useChatMessages";
import { useChatPhases } from "../../composables/useChatPhases";
import { useAgentTimeout } from "../../composables/useAgentTimeout";
import { useConversationMonitor } from "../../composables/useConversationMonitor";
import { useChatEventHandlers } from "../../composables/useChatEventHandlers";
import { useChatState } from "../../composables/useChatState";

export default {
  name: "NewChatBody",
  components: {
    ChatStart,
    ChatAI,
    ChatHuman,
    ChatEnd,
    AgentTimeout,
    ChatMessageRenderer,
    ChatWaitingBar,
  },

  props: {
    styles: {
      type: Object,
      default: () => ({ buttonColor: "#000000" }),
    },
    companyId: {
      type: String,
      required: true,
    },
    isExpanded: {
      type: Boolean,
      default: false,
    },
  },

  emits: [
    "form-submitted",
    "mood-submitted",
    "chat-complete",
    "chat-restarted",
    "customer-changed",
    "conversation-changed",
    "message-sent",
    "agent-joined",
    "navigate-to-faq", // 🆕 NEW: Add FAQ navigation emit
  ],

  setup(props, { emit }) {
    console.log("🔍 NEW-CHAT-BODY: Setup with props:", {
      companyId: props.companyId,
      companyIdType: typeof props.companyId,
    });

    const chatContainer = ref(null);
    const currentAgentInfo = ref(null);
    const componentKey = ref(Date.now());

    // Use composables for different concerns
    const messageHandlers = useChatMessages();
    const phaseManager = useChatPhases();
    const timeoutManager = useAgentTimeout();
    const conversationMonitor = useConversationMonitor();

    // Get reactive state from global state manager
    const {
      messages,
      customer,
      conversation,
      customerName,
      currentPhase,
      subscribeToState,
    } = useChatState();

    const forceComponentUpdate = () => {
      componentKey.value = Date.now();
      console.log("🔄 Component key updated:", componentKey.value);
    };

    // Event handlers with dependencies injected
    const eventHandlers = useChatEventHandlers(
      messageHandlers.addMessage,
      phaseManager.transitionToPhase,
      timeoutManager.startAgentTimeoutTimer,
      timeoutManager.clearAgentTimeoutTimer,
      timeoutManager.handleTimeoutResolved
    );

    const containerClasses = computed(() => {
      return props.isExpanded ? "mb-0" : "mb-30";
    });

    // Computed properties
    const showWaitingBar = computed(() => {
      return currentPhase.value === "human" && !hasAgentJoined.value;
    });

    const hasAgentJoined = computed(() => {
      return messages.value.some((msg) => msg.type === "agent-joined");
    });

    const currentPhaseComponent = computed(() => {
      if (timeoutManager.showAgentTimeout.value) {
        return "AgentTimeout";
      }

      const phaseMap = {
        start: "ChatStart",
        ai: "ChatAI",
        human: "ChatHuman",
        end: "ChatEnd",
      };

      return phaseMap[currentPhase.value] || "ChatStart";
    });

    // Get transition form state from phase manager
    const { showTransitionForm } = phaseManager;

    // Utility functions
    const scrollToBottom = () => {
      nextTick(() => {
        if (chatContainer.value) {
          chatContainer.value.scrollTop = chatContainer.value.scrollHeight;
        }
      });
    };

    // 🆕 NEW: Handle FAQ navigation from ChatMessageRenderer
    const handleFaqNavigation = (faqEvent) => {
      console.log('🎯 FAQ Navigation requested in NewChatBody:', faqEvent);
      
      // Emit to parent (NewChatScreen)
      emit('navigate-to-faq', {
        faqData: faqEvent.faqData,
        category: faqEvent.category,
        directToFaq: true // Flag to go directly to FAQ detail
      });
    };

    // ✅ ENHANCED: Complete restart handler with proper subscription cleanup
    const handleRestartWithTransition = () => {
      console.log("🔄 Handling restart with transition");

      // ✅ STEP 1: IMMEDIATELY stop all subscriptions to prevent interference
      console.log("🛑 Stopping ALL subscriptions before restart");

      // ✅ CRITICAL: Stop conversation monitoring (THIS IS THE KEY FIX)
      if (conversationMonitor?.stopStatusMonitoring) {
        conversationMonitor.stopStatusMonitoring();
        console.log("📡 Conversation monitoring stopped");
      }

      // ✅ STEP 2: Clear any timeouts
      if (timeoutManager?.clearAgentTimeoutTimer) {
        timeoutManager.clearAgentTimeoutTimer();
      }

      // ✅ STEP 3: Call the restart handler
      const restartResult = eventHandlers.handleRestartChat();

      // ✅ STEP 4: Force phase transition (with delay to ensure cleanup)
      setTimeout(() => {
        phaseManager.transitionToPhase("start");
        console.log("🏠 Forced transition to start phase");
      }, 100);

      // ✅ STEP 5: Force component re-render
      forceComponentUpdate();

      // ✅ STEP 6: Clear component-level state
      currentAgentInfo.value = null;

      // ✅ STEP 7: Emit to parent
      emit("chat-restarted");

      console.log("🏠 Complete restart finished - all subscriptions stopped");
    };

    // ✅ FIXED: Handle end chat request from header button - Direct approach
    const handleEndChatFromHeader = async () => {
      console.log("🔚 End chat requested from header button");

      // Only allow ending chat if we're in AI or human phase
      if (currentPhase.value !== "ai" && currentPhase.value !== "human") {
        console.log(
          "⚠️ Cannot end chat - not in active chat phase:",
          currentPhase.value
        );

        // If already in end phase, don't do anything
        if (currentPhase.value === "end") {
          console.log("ℹ️ Already in end phase");
          return;
        }

        // If in start phase, user hasn't started chatting yet
        if (currentPhase.value === "start") {
          console.log("ℹ️ Chat not started yet");
          return;
        }

        return;
      }

      // Check if we have the required objects
      if (!customer.value || !conversation.value) {
        console.log(
          "⚠️ Cannot end chat - missing customer or conversation data",
          {
            hasCustomer: !!customer.value,
            hasConversation: !!conversation.value,
          }
        );

        // Fallback: Ask user for confirmation directly
        const confirmed = confirm("Möchten Sie den Chat wirklich beenden?");
        if (confirmed) {
          console.log(
            "✅ User confirmed chat end via fallback - transitioning to end phase"
          );
          phaseManager.transitionToPhase("end");
        }
        return;
      }

      try {
        // ✅ Ask user for confirmation first
        const confirmed = confirm("Möchten Sie den Chat wirklich beenden?");
        if (!confirmed) {
          console.log("❌ User cancelled chat end");
          return;
        }

        console.log(
          "✅ User confirmed chat end - updating conversation status directly"
        );

        // ✅ FIXED: Import the markChatEnded function
        const { markChatEnded } = await import("../../lib/appwriteChat.js");

        // ✅ FIXED: Directly update conversation status to 'ended' in Appwrite
        await markChatEnded(conversation.value.$id);

        console.log("🏁 Conversation marked as ended in Appwrite");

        // ✅ FIXED: Transition to end phase directly (no AI message needed)
        phaseManager.transitionToPhase("end");

        console.log("✅ Chat ended successfully - transitioned to end phase");
      } catch (error) {
        console.error("❌ Error ending chat from header:", error);

        // Fallback: Ask user for confirmation directly
        const confirmed = confirm(
          "Es gab einen Fehler. Möchten Sie den Chat trotzdem beenden?"
        );
        if (confirmed) {
          console.log("✅ User confirmed chat end via error fallback");
          // Direct transition to end phase
          phaseManager.transitionToPhase("end");
        }
      }
    };

    // Enhanced event handlers that emit to parent
    const handleFormSubmittedWithEmit = (data) => {
      console.log("🔍 NEW-CHAT-BODY: Form submitted with data:", data);
      const result = eventHandlers.handleFormSubmitted(data);
      emit("form-submitted", data);
      return result;
    };

    const handleMoodSubmittedWithEmit = async (moodData) => {
      const result = await eventHandlers.handleMoodSubmitted(moodData);
      emit("mood-submitted", {
        ...moodData,
        customer: customer.value,
        conversation: conversation.value,
      });
      setTimeout(() => emit("chat-complete"), 2000);
      return result;
    };

    const handleAgentJoinedWithEmit = (agentData) => {
      currentAgentInfo.value = agentData.agentInfo;
      const result = eventHandlers.handleAgentJoined(agentData);
      emit("agent-joined", agentData);
      return result;
    };

    const handleChatCompleteWithEmit = () => {
      eventHandlers.handleChatComplete();
      emit("chat-complete");
    };

    // Watchers
    watch(customer, (newCustomer) => {
      emit("customer-changed", newCustomer);
    });

    watch(conversation, (newConversation) => {
      emit("conversation-changed", newConversation);
      if (newConversation) {
        console.log(
          "📡 Starting conversation monitoring for:",
          newConversation.$id
        );
        conversationMonitor.startStatusMonitoring(newConversation.$id);
      } else {
        console.log("📡 Stopping conversation monitoring");
        conversationMonitor.stopStatusMonitoring();
      }
    });

    // ✅ ENHANCED: Watch for phase changes and force update when needed
    watch(currentPhase, (newPhase, oldPhase) => {
      console.log(`📱 Phase changed: ${oldPhase} → ${newPhase}`);

      // Force component update when transitioning to start from end
      if (oldPhase === "end" && newPhase === "start") {
        console.log("🔄 Forcing component update for end → start transition");
        forceComponentUpdate();
      }
    });

    watch(
      messages,
      () => {
        scrollToBottom();
      },
      { deep: true }
    );

    // Lifecycle
    onMounted(() => {
      console.log("💬 Chat body mounting with composables...");
      console.log("🔍 NEW-CHAT-BODY: Mounted with companyId:", props.companyId);
      subscribeToState();

      console.log(
        "📡 Real-time message subscription will auto-manage based on conversation state"
      );

      console.log(
        "💬 Chat body initialized with composables and real-time messages"
      );
    });

    onBeforeUnmount(() => {
      console.log("💬 Chat body unmounting...");
      conversationMonitor.stopStatusMonitoring();
    });

    // Return everything needed by template
    return {
      // Refs
      chatContainer,
      currentAgentInfo,
      componentKey,

      // Reactive State
      messages,
      customer,
      conversation,
      customerName,
      currentPhase,

      // Computed
      showWaitingBar,
      hasAgentJoined,
      showTransitionForm,
      currentPhaseComponent,
      containerClasses,

      // Composable handlers
      messageHandlers,
      phaseManager,
      timeoutManager,
      conversationMonitor,

      // Enhanced event handlers with parent emits
      eventHandlers: {
        ...eventHandlers,
        handleFormSubmitted: handleFormSubmittedWithEmit,
        handleMoodSubmitted: handleMoodSubmittedWithEmit,
        handleAgentJoined: handleAgentJoinedWithEmit,
        handleChatComplete: handleChatCompleteWithEmit,
        handleRestartChat: handleRestartWithTransition, // ✅ Use enhanced handler
      },

      // Add the enhanced handlers for template and external access
      handleRestartWithTransition,
      handleEndChatFromHeader, // ✅ NEW: Exposed for NewChatScreen to call
      handleFaqNavigation, // 🆕 NEW: FAQ navigation handler

      // Utilities
      scrollToBottom,
    };
  },
};
</script>

<style scoped>
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 3px;
}
</style>